/**
 * PublicBodyService - Javni organi: dodavanje, ažuriranje, odobravanje, odbijanje, invalidacija, listanje.
 */

import ApiService, { clearLoginDataCache } from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import OrganizationService, {
  normalizeOrganizationAdditionalInfo,
} from '@/shared/services/OrganizationService';
import PublicBodyRegisterCatalogService from './PublicBodyRegisterCatalogService';
import {
  buildOrganizationAddApiBody,
  resolveOrganizationAddDefaults,
} from './organizationAddPayload';
import { buildOrganizationRegistrationApiBody } from './organizationRegistrationPayload';
import { getApiErrorCode, getApiErrorMessage, toApiError } from '@/shared/utils/apiErrorMessage';
import {
  API_ERROR_CODES,
  API_ERROR_MESSAGES,
  INSTITUTION_EXISTS_EX_MESSAGE,
} from '@/shared/constants/apiResponseCodes';

/** Kontakt osoba (ne telefon/email institucije) za save-additional-info. */
function pickContactAdditionalInfo(data = {}) {
  return normalizeOrganizationAdditionalInfo({
    contactFirstName: data.contactFirstName,
    contactLastName: data.contactLastName,
    userEmail: data.userEmail,
    userPhone: data.userPhone,
    first_name: data.first_name,
    last_name: data.last_name,
  });
}

function buildPublicBodyAddPayload(data, defaults = {}) {
  return buildOrganizationAddApiBody(data, defaults);
}

function formatPublicBodyApiError(error, fallbackMessage) {
  const code = getApiErrorCode(error);
  if (code === API_ERROR_CODES.OrganizationExistsEx) {
    return INSTITUTION_EXISTS_EX_MESSAGE;
  }
  if (code && API_ERROR_MESSAGES[code]) {
    return API_ERROR_MESSAGES[code];
  }

  const status = error?.response?.status;
  const data = error?.response?.data;
  const backendMessage = String(data?.message || '').trim();

  if (status === 404) {
    if (backendMessage.includes('organization_register') || backendMessage.includes('register_id')) {
      return 'Изабрани тип институције не постоји у систему. Освежите страницу и изаберите поново.';
    }
    return backendMessage || 'Ресурс није пронађен.';
  }

  return getApiErrorMessage(error, fallbackMessage);
}

async function saveContactAdditionalInfo(organizationId, data) {
  const additional = pickContactAdditionalInfo(data);
  const hasAdditional =
    additional.first_name ||
    additional.last_name ||
    additional.phone ||
    additional.email;

  if (!hasAdditional) return;

  try {
    await OrganizationService.saveAdditionalInfo({
      organization_id: organizationId,
      ...additional,
    });
  } catch (additionalError) {
    const msg =
      additionalError.message || 'Грешка при чувању контакт података';
    throw new Error(`${msg} (организација је креирана, ID: ${organizationId})`);
  }
}

const mapPublicBody = (pb) => ({
  id: pb.id,
  name: pb.name,
  naziv: pb.name,
  registrationNumber: pb.registration_number,
  registration_number: pb.registration_number,
  maticniBroj: pb.registration_number,
  register_id: pb.register_id,
  registry: pb.register_name,
  register_name: pb.register_name,
  registar: pb.register_name,
  ...pb
});

class PublicBodyService {
  /**
   * Građanska prijava institucije: POST /public-body/register (org + team lead + kontakt).
   */
  async registerPublicBody(data) {
    try {
      const response = await ApiService.post(
        API_ENDPOINTS.PUBLIC_BODY.REGISTER_BY_USER,
        buildOrganizationRegistrationApiBody(data),
      );
      const created = response.data ?? response;
      const organizationId = created?.id ?? created?.organization_id;

      if (!organizationId) {
        throw new Error('Сервер није вратио ID нове институције');
      }

      clearLoginDataCache();
      return created;
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 404) {
        throw new Error(formatPublicBodyApiError(error, 'Јавни орган није пронађен'));
      }
      if (error.response && error.response.status === 400) {
        throw toApiError(error, formatPublicBodyApiError(error, 'Neispravni podaci јавног органа'));
      }
      if (error.response && error.response.status === 409) {
        throw new Error(formatPublicBodyApiError(error, INSTITUTION_EXISTS_EX_MESSAGE));
      }
      throw error;
    }
  }

  /**
   * Admin: POST /public-body/add, zatim POST /organization/save-additional-info (kontakt osoba).
   */
  async addPublicBody(data) {
    try {
      if (!data?.name?.trim()) throw new Error('Naziv јавног органа je obavezan');

      const registerDefaults = await resolveOrganizationAddDefaults(
        data,
        () => PublicBodyRegisterCatalogService.getActive(),
      );

      const response = await ApiService.post(
        API_ENDPOINTS.PUBLIC_BODY.ADD,
        buildPublicBodyAddPayload(data, registerDefaults),
      );
      const created = response.data ?? response;
      const organizationId = created?.id ?? created?.organization_id;

      if (!organizationId) {
        throw new Error('Сервер није вратио ID нове институције');
      }

      await saveContactAdditionalInfo(organizationId, data);
      return created;
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 404) {
        throw new Error(formatPublicBodyApiError(error, 'Јавни орган није пронађен'));
      }
      if (error.response && error.response.status === 400) {
        throw toApiError(error, formatPublicBodyApiError(error, 'Neispravni podaci јавног органа'));
      }
      if (error.response && error.response.status === 409) {
        throw new Error(formatPublicBodyApiError(error, INSTITUTION_EXISTS_EX_MESSAGE));
      }
      throw error;
    }
  }

  async updatePublicBody(publicBodyId, data) {
    try {
      if (!publicBodyId) throw new Error('ID јавног органа je obavezan');
      if (!data?.name?.trim()) throw new Error('Naziv јавног органа je obavezan');

      const payload = buildOrganizationAddApiBody(data, {
        registerId: data.register_id?.trim() || data.registerId?.trim() || undefined,
        registrationNumber:
          data.registration_number?.trim() ||
          data.registrationNumber?.trim() ||
          undefined,
      });

      const response = await ApiService.put(
        API_ENDPOINTS.PUBLIC_BODY.UPDATE(publicBodyId),
        payload
      );

      const additional = pickContactAdditionalInfo(data);
      const hasAdditional =
        additional.first_name ||
        additional.last_name ||
        additional.phone ||
        additional.email;

      if (hasAdditional) {
        await OrganizationService.saveAdditionalInfo({
          organization_id: publicBodyId,
          ...additional,
        });
      }

      if (response.data) return response.data;
      return { success: true, id: publicBodyId };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 404) throw new Error('Јавни орган није пронађен');
      if (error.response && error.response.status === 400) {
        throw toApiError(error, formatPublicBodyApiError(error, 'Neispravni podaci јавног органа'));
      }
      throw error;
    }
  }

  async approvePublicBody(publicBodyId) {
    try {
      if (!publicBodyId) throw new Error('ID јавног органа je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.PUBLIC_BODY.APPROVE, { organization_id: publicBodyId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za odobravanje јавних органа');
      if (error.response && error.response.status === 404) throw new Error('Јавни орган није пронађен');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri odobravanju јавног органа');
      throw error;
    }
  }

  async rejectPublicBody(publicBodyId) {
    try {
      if (!publicBodyId) throw new Error('ID јавног органа je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.PUBLIC_BODY.REJECT, { organization_id: publicBodyId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za odbijanje јавних органа');
      if (error.response && error.response.status === 404) throw new Error('Јавни орган није пронађен');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri odbijanju јавног органа');
      throw error;
    }
  }

  async invalidatePublicBody(publicBodyId) {
    try {
      if (!publicBodyId) throw new Error('ID јавног органа je obavezan');
      const response = await ApiService.post(API_ENDPOINTS.PUBLIC_BODY.INVALIDATE, { organization_id: publicBodyId });
      if (response.data) return response.data;
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      if (error.response && error.response.status === 403) throw new Error('Nemate dozvolu za invalidaciju јавних органа');
      if (error.response && error.response.status === 404) throw new Error('Јавни орган није пронађен');
      if (error.response && error.response.status === 400) throw new Error(error.response.data?.message || 'Greška pri invalidaciji јавног органа');
      throw error;
    }
  }

  async getAllPublicBodies() {
    try {
      const response = await ApiService.get(API_ENDPOINTS.PUBLIC_BODY.LIST);
      if (response.data) {
        if (response.data.public_bodies && Array.isArray(response.data.public_bodies)) return response.data.public_bodies.map(mapPublicBody);
        if (Array.isArray(response.data)) return response.data;
      }
      return [];
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      throw error;
    }
  }

  async getApprovalListPublicBodies() {
    try {
      const response = await ApiService.get(API_ENDPOINTS.PUBLIC_BODY.APPROVAL_LIST);
      if (response.data) {
        if (response.data.public_bodies && Array.isArray(response.data.public_bodies)) return response.data.public_bodies.map(mapPublicBody);
        if (Array.isArray(response.data)) return response.data;
      }
      return [];
    } catch (error) {
      if (error.response && error.response.status === 401) throw new Error('Korisnik nije prijavljen');
      throw error;
    }
  }
}

const publicBodyServiceInstance = new PublicBodyService();
export default publicBodyServiceInstance;
